import { useEffect, useRef, useState } from 'react'

import {
  GAMES,
  MASTERMIND_SIZES,
  TEDDIES,
  type MastermindSize,
  type Peg,
  type Played,
} from '@hazel/shared'

import { Button } from '../components/Button.js'
import { Card } from '../components/Card.js'
import { KeyCap } from '../components/KeyCap.js'
import { KeyLegend } from '../components/KeyLegend.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { Announce } from './Announce.js'
import { LeaveGame } from './LeaveGame.js'
import { MastermindPeg, PlayedPeg } from './MastermindPeg.js'
import { BOARD_ROW, MASTERMIND_LAYOUT, pegsFor, stepFrom, teddyAtKey } from './mastermindLayout.js'
import { Milestone } from './Milestone.js'
import { PlayAgain } from './PlayAgain.js'
import { useCueOnChange, useRoundEndCues, useRoundMusic } from './gameSound.js'
import { TRACK_FOR_GAME } from '../sound/cues.js'
import { useBarTitle } from '../layouts/bar-title.js'
import { digitFrom, useGameKeys } from './useGameKeys.js'
import { useLeaveOnEscape } from './useLeaveOnEscape.js'
import { useMastermind, type MastermindConfigFromRound } from './useMastermind.js'
import { useRound, type DealtRound } from './useRound.js'
import { useRoundFocus } from './useRoundFocus.js'

/**
 * What each size is called, and what changes about it.
 *
 * The second line is the point, and the third size's is the one that matters:
 * allowing a repeat is not more of the same, it is the removal of the rule she
 * has been reasoning with. Up to then, settling a teddy rules it out
 * everywhere else. She should meet that in the choice rather than discover it
 * halfway through a round that will not come out.
 */
const SIZE_LABELS: Readonly<Record<MastermindSize, { name: string; means: string }>> = {
  gentle: { name: 'Gentle', means: '4 teddies, all different' },
  middling: { name: 'Middling', means: '5 teddies, all different' },
  proper: { name: 'The proper one', means: '5 teddies, one can come twice' },
}

/**
 * How hard a row, asked before there is one.
 *
 * She picks, exactly as she picks a grid in Stripey's Sudoku and a picture in
 * Maritza's. Every size fits every screen — unlike a mosaic, this board is
 * bounded by its ten rows rather than by its width — so nothing is withheld
 * from a phone.
 */
function ChooseSize({ onChoose }: { onChoose: (size: MastermindSize) => void }) {
  const leaveOnEscape = useLeaveOnEscape()

  useGameKeys((event) => {
    if (event.key === 'Escape') return void leaveOnEscape(false)

    const digit = digitFrom(event)
    if (!digit) return

    const chosen = MASTERMIND_SIZES[Number(digit) - 1]
    if (chosen !== undefined) onChoose(chosen)
  })

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-5 [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:gap-3">
      <Card className="flex w-full flex-col items-center gap-4">
        <h2 className="text-(length:--text-heading)">How hard a row?</h2>

        <div className="flex w-full flex-col gap-2 [[data-scale=tv]_&]:gap-1">
          {MASTERMIND_SIZES.map((option, index) => (
            <Button
              key={option}
              variant="secondary"
              block
              onClick={() => onChoose(option)}
              aria-label={`${SIZE_LABELS[option].name}, ${SIZE_LABELS[option].means}. Press ${index + 1}`}
            >
              <span className="flex w-full items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <span aria-hidden="true" className="[@media(pointer:coarse)]:hidden">
                    <KeyCap>{index + 1}</KeyCap>
                  </span>
                  {SIZE_LABELS[option].name}
                </span>
                <span className="text-ink-muted text-(length:--text-small)">
                  {SIZE_LABELS[option].means}
                </span>
              </span>
            </Button>
          ))}
        </div>
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={[
          { keys: ['1', '–', String(MASTERMIND_SIZES.length)], does: 'Pick a row' },
          LEAVE_SHORTCUT,
        ]}
      />
    </div>
  )
}

export function Mastermind() {
  // The bar carries the name while she is here, in place of "Hazel's Games".
  useBarTitle(GAMES.mastermind.title)

  /*
    The screen's music, not the round's — so another go does not restart the
    track. Borrowing Stripey's loop, as Maritza's does: all three are quiet
    sit-down puzzles and the track suits any of them. A `mastermind` track is
    one line here the day there is a file for it.
  */
  useRoundMusic(TRACK_FOR_GAME.mastermind)

  const [size, setSize] = useState<MastermindSize | null>(null)

  if (size === null) return <ChooseSize onChoose={setSize} />

  return <MastermindRound size={size} />
}

function MastermindRound({ size }: { size: MastermindSize }) {
  const { round, again, failed } = useRound<MastermindConfigFromRound>({
    game: 'mastermind',
    size,
  })

  if (failed) {
    return (
      <p role="alert" className="text-coral py-16 text-center">
        We couldn’t start a game just now. Have another go in a moment.
      </p>
    )
  }

  if (!round) {
    return (
      <p role="status" className="py-16 text-center">
        Hiding a row…
      </p>
    )
  }

  return <MastermindBoard key={round.seed} round={round} size={size} onAgain={again} />
}

function MastermindBoard({
  round,
  size,
  onAgain,
}: {
  round: DealtRound<MastermindConfigFromRound>
  size: MastermindSize
  onAgain: () => void
}) {
  const leaveOnEscape = useLeaveOnEscape()
  const game = useMastermind(round, onAgain)
  const { view } = game
  const board = useRoundFocus<HTMLDivElement>(round.seed, view.status !== 'playing')

  /** Which place in the row the keyboard is pointing at. */
  const [cursor, setCursor] = useState(0)

  useCueOnChange(cursor, 'move')
  useRoundEndCues(view.status !== 'playing', game.milestone)

  const layout = MASTERMIND_LAYOUT[size]

  /** The place buttons, so the cursor can carry real focus with it. */
  const places = useRef(new Map<number, HTMLButtonElement>())
  const tryButton = useRef<HTMLButtonElement>(null)

  const full = view.building.every((peg) => peg !== null)

  /*
   * When the row fills up, focus moves to "Try it".
   *
   * This is the whole reason that button exists rather than Enter being
   * handled here. `useGameKeys` refuses Enter while a button has focus —
   * correctly, and exactly as CLAUDE.md requires — and the cursor puts real
   * focus on the place under the ring. So an Enter handler in this file would
   * be a legend naming a key that cannot work, which is DOL-111 exactly.
   *
   * Moving focus instead means Enter submits by *activating a control*, which
   * is what Enter is for. No rule is bent and the legend stays true.
   */
  useEffect(() => {
    if (full && view.status === 'playing') tryButton.current?.focus({ preventScroll: true })
  }, [full, view.status, view.played.length])

  const goes = view.played.length

  /*
   * And back to the start of the new row once a go is spent.
   *
   * Not tidiness — without it focus is nowhere. Spending a go empties the row,
   * which disables "Try it", and **a browser blurs an element the moment it
   * becomes disabled**: focus drops to `<body>`. The ring would go on pointing
   * at a peg while Tab started again from the top of the document, which on
   * the television is the whole way she gets around.
   *
   * jsdom does not blur on disable, so the unit suite cannot see any of this.
   * The check for it lives in `television.spec.ts`, against a real browser,
   * and was watched failing before this existed.
   *
   * Place one rather than wherever she was: the row is empty again, so the
   * place she last touched is no more hers than any other. The cursor is put
   * back by `spendGo` below rather than here — moving it in an effect would
   * set state during one render to cause another, which is a cascade the
   * linter is right to refuse.
   */
  useEffect(() => {
    if (goes === 0 || view.status !== 'playing') return

    places.current.get(0)?.focus({ preventScroll: true })
  }, [goes, view.status])

  /** Spends a go, and puts the cursor back at the start of the new row. */
  function spendGo(): void {
    setCursor(0)
    game.tryIt()
  }

  function moveTo(next: number | null): void {
    if (next === null) return

    setCursor(next)

    // Focus goes where the ring goes, every time — or the next key lands on a
    // place nobody is pointing at.
    const target = places.current.get(next)
    target?.focus()
    if (document.activeElement !== target) board.current?.focus()
  }

  /** Puts a teddy in a place — by default the one under the cursor. */
  function put(peg: Peg, at: number = cursor): void {
    game.place(at, peg)

    // On to the next empty place, so a row can be built without touching the
    // arrows. `setCursor` has not taken effect yet, so this works from `at`
    // rather than from the cursor — the bug Maritza's `put` was fixed for.
    const next = nextEmptyAfter(at)
    if (next !== null) moveTo(next)
  }

  function nextEmptyAfter(at: number): number | null {
    const pegs = pegsFor(size)

    for (let step = 1; step <= pegs; step++) {
      const place = (at + step) % pegs
      if (view.building[place] === null && place !== at) return place
    }

    return null
  }

  useGameKeys((event) => {
    if (event.key === 'Escape') {
      // One rung at a time: clear the row she is building, and leave only when
      // there is nothing left to back out of.
      const somethingToClear = view.building.some((peg) => peg !== null)
      if (somethingToClear) {
        event.preventDefault()
        game.clear()
        return
      }

      return void leaveOnEscape(false)
    }

    if (view.status !== 'playing') {
      if (event.key === 'Enter') game.again()
      return
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      return moveTo(stepFrom(cursor, event.key, size))
    }

    if (event.key === 'Backspace') {
      event.preventDefault()
      return game.take(cursor)
    }

    if (event.key === 'r' || event.key === 'R') {
      event.preventDefault()
      return game.clear()
    }

    const digit = digitFrom(event)
    if (!digit) return

    const which = teddyAtKey(digit, view.palette.length)
    if (which === null) return

    event.preventDefault()
    put(view.palette[which] as Peg)
  })

  const answer = view.answer

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-4 [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1">
      <Card
        ref={board}
        tabIndex={-1}
        className="outline-none flex w-full flex-col items-center gap-4 [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1 [[data-scale=tv]_&]:flex-row [[data-scale=tv]_&]:items-stretch [[data-scale=tv]_&]:gap-6"
      >
        <div className="contents [[data-scale=tv]_&]:order-2 [[data-scale=tv]_&]:flex [[data-scale=tv]_&]:w-80 [[data-scale=tv]_&]:flex-none [[data-scale=tv]_&]:flex-col [[data-scale=tv]_&]:justify-center [[data-scale=tv]_&]:gap-6">
          {/*
            What she was told, never how close she is. "Two in the right place"
            is the game; "nearly there" would be the answer leaking out one
            adjective at a time.
          */}
          <Announce>
            {[
              view.status === 'playing'
                ? (lastAnswer(view.played) ?? `${view.goesLeft} goes. Pick ${view.pegs} teddies.`)
                : view.status === 'found'
                  ? `You got it in ${view.played.length}.`
                  : `Out of goes. It was ${namesOf(answer)}.`,
              game.milestone ? `That’s ${game.milestone} games played.` : '',
            ]
              .filter(Boolean)
              .join(' ')}
          </Announce>

          {view.status === 'playing' ? (
            <p className="text-ink-muted text-(length:--text-small) [[data-scale=tv]_&]:text-(length:--text-body)">
              {view.goesLeft} {view.goesLeft === 1 ? 'go' : 'goes'} left
            </p>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <p className="font-display text-(length:--text-title)">
                {view.status === 'found' ? 'You got it!' : 'Out of goes'}
              </p>
              {view.status === 'out-of-goes' && answer ? (
                <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">
                  It was {namesOf(answer)}.
                </p>
              ) : null}
              {game.milestone ? <Milestone games={game.milestone} /> : null}
              <PlayAgain label="Another row" does="for another row" onAgain={game.again} />
            </div>
          )}
        </div>

        <div className="flex w-full min-w-0 flex-col items-center gap-2 [[data-scale=tv]_&]:order-1 [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1 [[data-scale=tv]_&]:justify-center">
          {/*
            Every go on screen at once, oldest at the top.

            Not a scrolling list, deliberately. For a logic puzzle, seeing
            everything at once *is* the task — deduction means holding
            constraints together, and a history she has to scroll back through
            is a memory test wearing a deduction puzzle's clothes. The same
            argument `clueLayout.ts` makes about Rocky's clues.
          */}
          <ol className="flex w-full flex-col items-center gap-1" aria-label="Your goes so far">
            {view.played.map((played, go) => (
              <li key={go} className={BOARD_ROW}>
                <span
                  aria-hidden="true"
                  className="text-ink-muted justify-self-end text-(length:--text-small) [[data-scale=tv]_&]:text-(length:--text-body)"
                >
                  {go + 1}
                </span>

                <span
                  className={`grid gap-1 ${layout.rowClassName}`}
                  aria-label={`Go ${go + 1}: ${namesOf(played.row)}`}
                >
                  {played.row.map((peg, at) => (
                    <PlayedPeg
                      key={at}
                      peg={peg}
                      pegClassName={`${layout.pegClassName} ${layout.tvPegClassName}`}
                    />
                  ))}
                </span>

                {/*
                  Said in words, not in little black and white pegs.

                  The classic board game answers with pegs of its own, which is
                  a second code to learn before the first one can be worked on
                  — and unreadable across a room. Numbers with their meaning
                  attached say the same thing and can be read aloud.
                */}
                <span className="justify-self-start text-(length:--text-small) [[data-scale=tv]_&]:text-(length:--text-body)">
                  <strong>{played.score.exact}</strong> right place,{' '}
                  <strong>{played.score.colour}</strong> wrong place
                </span>
              </li>
            ))}
          </ol>

          {view.status === 'playing' ? (
            <div className="mt-2 flex w-full flex-col items-center gap-3">
              {/*
                The same three columns as a played row, so her pegs sit directly
                under the pegs she is comparing them against. Reading down the
                column to see what changed is the whole method.
              */}
              <div className={BOARD_ROW}>
                <span
                  aria-hidden="true"
                  className="text-ink-muted justify-self-end text-(length:--text-small) [[data-scale=tv]_&]:text-(length:--text-body)"
                >
                  {view.played.length + 1}
                </span>

                <span
                  className={`grid gap-1 ${layout.rowClassName}`}
                  aria-label="The row you are building"
                >
                  {view.building.map((peg, at) => (
                    <MastermindPeg
                      key={at}
                      at={at}
                      peg={peg}
                      cursor={at === cursor}
                      pegClassName={`${layout.pegClassName} ${layout.tvPegClassName}`}
                      ref={(element) => {
                        if (element) places.current.set(at, element)
                        else places.current.delete(at)
                      }}
                      onPick={(element) => {
                        setCursor(at)
                        element?.focus()
                        // Tapping a filled place takes the teddy out, which is
                        // the only thing a tap could sensibly mean: there is no
                        // "next teddy" to cycle to that she has not chosen.
                        if (view.building[at] !== null) game.take(at)
                      }}
                    />
                  ))}
                </span>

                {/* Empty, and it has to be there: it is the column an answer
                    will fill once she has spent the go, and the thing that
                    keeps her pegs under the played ones until then. */}
                <span />
              </div>

              <Button
                ref={tryButton}
                onClick={spendGo}
                disabled={!full}
                aria-label={full ? 'Try it' : `Pick ${view.pegs} teddies first`}
              >
                Try it
              </Button>

              {/*
                The teddies she picks from, numbered the way the picker's games
                are so the same habit works twice.
              */}
              <ul
                className="flex flex-wrap items-center justify-center gap-2"
                aria-label="Teddies to choose from"
              >
                {view.palette.map((peg, index) => (
                  <li key={peg}>
                    <Button
                      variant="secondary"
                      onClick={() => put(peg)}
                      aria-label={`${TEDDIES[peg].name}. Press ${index + 1}`}
                    >
                      <span className="flex items-center gap-2">
                        <span aria-hidden="true" className="[@media(pointer:coarse)]:hidden">
                          <KeyCap>{index + 1}</KeyCap>
                        </span>
                        <PlayedPeg peg={peg} pegClassName={layout.pegClassName} />
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={
          view.status === 'playing'
            ? [
                { keys: ['1', '–', String(view.palette.length)], does: 'Put a teddy in' },
                { keys: ['←', '→'], does: 'Move along the row' },
                { keys: ['Backspace'], does: 'Take one out' },
                { keys: ['Enter'], does: 'Try it' },
                { keys: ['R'], does: 'Start the row again' },
                LEAVE_SHORTCUT,
              ]
            : [{ keys: ['Enter'], does: 'Play again' }, LEAVE_SHORTCUT]
        }
      />
    </div>
  )
}

/** The teddies in a row, by name, for anything that has to say it aloud. */
function namesOf(row: readonly Peg[] | null): string {
  if (!row) return 'a row of teddies'

  return row.map((peg) => TEDDIES[peg].name).join(', ')
}

/** What the last go was told, for the live region. */
function lastAnswer(played: readonly Played[]): string | null {
  const last = played[played.length - 1]
  if (!last) return null

  return `${last.score.exact} in the right place, ${last.score.colour} in the wrong place.`
}
